// Client-side Sentry initialization.
//
// IMPORTANT: this must live in `instrumentation-client.ts` (NOT `sentry.client.config.ts`).
// With Next.js + Turbopack, `sentry.client.config.ts` is no longer bundled/executed
// (it was only injected via Sentry's webpack plugin, which Turbopack does not run), so
// the browser SDK never initialized and all client-side captures were silent no-ops.
// `instrumentation-client.ts` is loaded natively by Next.js regardless of bundler.
// https://nextjs.org/docs/app/api-reference/file-conventions/instrumentation-client

;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:channel/liste\",\"regex\":\"^/([^/]+)/liste$\",\"paramNames\":[\"channel\"]},{\"path\":\"/:channel/reiseziele\",\"regex\":\"^/([^/]+)/reiseziele$\",\"paramNames\":[\"channel\"]},{\"path\":\"/hotel/:name\",\"regex\":\"^/hotel/([^/]+)$\",\"paramNames\":[\"name\"]}],\"staticRoutes\":[{\"path\":\"/bestaetigung\"},{\"path\":\"/buchung\"},{\"path\":\"/gutschein/einloesung\"},{\"path\":\"/sentry-example-page\"}]}";globalThis["_sentryNextJsVersion"] = "16.1.6";import * as Sentry from "@sentry/nextjs";

const appEnv = process.env.NEXT_PUBLIC_APP_ENV || "development";

// Cypress injects `window.Cypress` into the app-under-test. Our E2E suite runs
// against a production build, so without this guard every test run reports its
// synthetic errors (e.g. test-driven radio-input clicks on checkout) into the
// production Sentry project. Skip Sentry entirely under Cypress.
const isCypress = typeof window !== "undefined" && "Cypress" in window;

// Literal fallback: NEXT_PUBLIC_* vars are inlined at build time, and this one
// only existed in local .env.local files — every CI build shipped a DSN-less
// (silently no-op) browser SDK while server-side Sentry kept working off the
// runtime pod env. The DSN is a public identifier, safe to hardcode.
const SENTRY_DSN =
  process.env.NEXT_PUBLIC_SENTRY_DSN ||
  "https://c5ba8532ec291c3592a7b8833d515619@o207141.ingest.us.sentry.io/4508041576972288";

Sentry.init({
  dsn: SENTRY_DSN,

  // Disable performance monitoring - we only use Sentry for error tracking
  tracesSampleRate: 0,

  // Only enable Sentry in production, and never under Cypress E2E runs.
  enabled: appEnv === "production" && !isCypress,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  // Known third-party errors that are not application bugs — dropped before
  // beforeSend runs so they never count against the quota.
  ignoreErrors: [
    // Network errors from client connection drops. These appear as either plain
    // messages ("Failed to fetch") or with the hostname appended by the browser
    // ("Failed to fetch (api.weg.de)"). Caught here rather than only in
    // beforeSend because some arrive via mechanism "generic" (React Query error
    // propagated to an error boundary) which the beforeSend mechanism check
    // would not filter.
    /^Failed to fetch/,
    /^Load failed/,
    /^NetworkError/,
    // Cookie consent manager (cookie-consent-manager-ui) fires this when the
    // PlatformData visit event arrives after its 1-minute deadline. Race between
    // CMP script init and React hydration — not a code bug, not user-visible.
    /Failed to flush event queue: visit event is missing/,
    // AppsFlyer SDK injected externally (GTM) into hotel pages. Not in our
    // bundle; originates in the external script on beforeunload. Match on the
    // shared `afWebUserId` token so it catches every engine's wording:
    //   Chrome:  "Cannot read properties of undefined (reading 'afWebUserId')"
    //   Firefox: "can't access property \"afWebUserId\", window.AF_SDK.PLUGINS.PBA is undefined"
    // The property is exclusively AppsFlyer's, so this cannot mask our own code.
    /afWebUserId/,
    // Chrome Mobile WebView 117 (Aug 2023) cannot parse our Turbopack bundle —
    // transpilation gap on severely outdated WebView versions. Kept narrow so
    // it does not swallow unrelated "Unexpected end of input" / "JSON Parse
    // error: Unexpected EOF" errors, which are truncated-response failures.
    /Unexpected token 'else'/,
    // Google Publisher Tags (pubads_impl.js) passing a non-Element to
    // IntersectionObserver. Google's ad script bug, not ours. The message
    // wording differs by engine, so match both:
    //   Chrome:        "Failed to execute 'observe' on 'IntersectionObserver'…"
    //   Firefox/WebKit: "…to IntersectionObserver.observe must be an instance of Element"
    /Failed to execute 'observe' on 'IntersectionObserver'/,
    /to IntersectionObserver\.observe must be an instance of Element/,
    // React DOM reconciliation mismatch in the CSW (Common Search Widget) on
    // hotel pages. Handled by React's error boundary — user-visible impact is
    // none. Match the engine-agnostic tail shared by Chrome ("Failed to execute
    // 'removeChild' on 'Node': …") and Firefox ("Node.removeChild: …"):
    /The node to be removed is not a child of this node/,
    // Safari variant of the same removeChild reconciliation error.
    /The object can not be found here/,
    // Native iOS in-app browsers (Facebook, and other WKWebView wrappers)
    // inject their own bridge script that reads window.webkit.messageHandlers.
    // It is absent outside a native app context, so the injected script throws.
    // Not in our bundle — the frames point at the injected inline script.
    // Also covers the older identical issue IKARUS-A9.
    /window\.webkit\.messageHandlers/,
  ],

  // Suppress network errors that leak as unhandled rejections from third-party
  // libraries (e.g. @lmn/brg-ui-tracking's HTTP transport). These are caused
  // by the client's connection dropping and are not application bugs.
  // Scoped to onunhandledrejection only — captureException paths remain visible
  // so CORS misconfigurations or other real bugs still surface in Sentry.
  beforeSend(event, hint) {
    const mechanism = event.exception?.values?.[0]?.mechanism?.type;
    if (mechanism === "onunhandledrejection") {
      const error = hint?.originalException;
      if (
        error instanceof TypeError &&
        // Safari includes the hostname in the message: "Load failed (api.weg.de)"
        // Chrome may include it too: "Failed to fetch (api.weg.de)" — use startsWith.
        (error.message.startsWith("Failed to fetch") ||
          error.message.startsWith("Load failed") ||
          error.message.startsWith("NetworkError"))
      ) {
        return null;
      }
    }
    return event;
  },
});

// Required by @sentry/nextjs v10 to instrument client-side navigations
// (also silences the build-time "ACTION REQUIRED" warning).
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
